import React, {useContext, useState} from "react";
import MarkdownContent from "../common/MarkdownContent";
import {Button, message, Tag} from "antd";
import SingleResult from "../../service/SingleResult";
import SbomProject from "../../domain/SbomProject";
import SbomComponentFinding from "../../domain/SbomComponentFinding";
import {AppContextContext, SbomComponentServiceContext} from "../../Contexts";
import {Permission} from "../../domain/auth/Permission";
import {formatFindingStatus} from "../../domain/FindingStatus";
import {formatFindingJustification} from "../../domain/FindingJustification";
import FindingStatusForm from "./FindingStatusForm";
import * as statusStyles from "./FindingStatus.module.css";
import * as styles from "./FindingStatusPanel.module.css";

interface FindingStatusPanelProps {
    sbomProject?: SingleResult<SbomProject>;
    finding: SbomComponentFinding;
    onFindingStatusSave: (finding: SbomComponentFinding) => void;
}

function FindingStatusPanel({sbomProject, finding, onFindingStatusSave}: FindingStatusPanelProps) {
    const appContext = useContext(AppContextContext);
    const sbomComponentService = useContext(SbomComponentServiceContext);
    const [editing, setEditing] = useState(false);

    const allowModification = appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG)
        || sbomProject?.permissions?.includes(Permission.SBOM_PROJECT__ANALYZE) === true;
    const project = sbomProject?.data;
    const hasDetails = Boolean(finding.justification || finding.impactStatement || finding.actionStatement || finding.analysisMarkdown);

    return <section className={styles.panel}>
        <div className={styles.header}>
            <div className={styles.title}>
                <span>Status</span>
                <Tag variant={"filled"} className={statusStyles['status-' + finding.status]}>{formatFindingStatus(finding.status)}</Tag>
            </div>
            {!editing && allowModification && <Button size={"small"} disabled={project === undefined} onClick={() => setEditing(true)}>Edit</Button>}
        </div>

        <div className={styles.content}>
            {editing && project
                ? <FindingStatusForm
                    key={finding.id}
                    allowModification={allowModification}
                    project={project}
                    finding={finding}
                    onCancel={onCancel}
                    onFinish={onFinishFindingEdit}
                />
                : renderReadOnly()}
        </div>
    </section>;

    function renderReadOnly() {
        if (!hasDetails) {
            return <div className={styles.empty}>No status details available.</div>;
        }

        return <div className={styles.fields}>
            {renderField("Justification", finding.justification && formatFindingJustification(finding.justification))}
            {renderField("Action statement", finding.actionStatement)}
            {renderField("Impact statement", finding.impactStatement)}
            {renderField("Analysis", finding.analysisMarkdown && <MarkdownContent children={finding.analysisMarkdown}/>, styles.markdown)}
        </div>;
    }

    function renderField(label: string, value?: React.ReactNode, valueClassName?: string) {
        if (!value) {
            return null;
        }

        return <>
            <div className={styles.label}>{label}</div>
            <div className={[styles.value, valueClassName].filter(Boolean).join(" ")}>{value}</div>
        </>;
    }

    function onCancel() {
        setEditing(false);
    }

    function onFinishFindingEdit(findingForEdit: SbomComponentFinding, values: any) {
        sbomComponentService.updateFinding(findingForEdit.id, values)
            .then(() => {
                message.success("Vulnerability status successfully updated.");

                setEditing(false);

                onFindingStatusSave(findingForEdit);
            });
    }
}

export default FindingStatusPanel;
